import { useCallback, useEffect } from 'react';

import { User } from '@coinweb/wallet-lib';
import { useQuery, type RefetchOptions, type QueryObserverResult } from '@tanstack/react-query';
import { create } from 'zustand';

import { Currency, isDev } from '@/constants';

export interface BetaInfo {
  limits?: {
    c1?: {
      min?: number;
      max?: number;
    };
    c2?: {
      min?: number;
      max?: number;
    };
  };
}

export interface OwnerInfo extends User {}

export interface ContentInfo {
  l1_type: number;
  shard: string;
  owner: OwnerInfo | string;
  owner_min_fee?: string;
  owner_percentage_fee?: number;
  order_life_time: number;
  close_order_timeout: number;
  jump_contract_id: string;
  jump_contract_method: string;
  jump_contract_jumps: number;
  preset_contract_id?: string;
  preset_claim_key?: string;
  logs: string[];
  dust_limit?: number;
  l1_contract_address?: string;
  l1_transfer_event_signature?: string;
  collateral_percentage_Int?: number;
  owner_collateral_fee_Int?: number;
  pact_life_time?: number;
}

export interface ParametersInfo {
  content: ContentInfo;
}

export interface InstanceInfo {
  parameters: ParametersInfo;
  instance_id: string;
  alias: string;
}

export interface PkgMirror {
  blake3: string;
  url: string;
}

export interface ModuleInfo {
  version: string;
  contract_module: string;
  pkg_mirror: PkgMirror;
  alias: string;
  instance: InstanceInfo;
}

export interface ContractAddress {
  id: string;
  module: ModuleInfo;
}

export interface ContractAddressIdOnly {
  id: string;
}

export interface TokenInfo {
  PRESET?: string;
  PRESET_BASE58?: string;
  PRESET_BECH32?: string;
  PRESET_BECH32M?: string;
  NETWORK_NAME: string;
  L1_TOKEN_ADDRESS?: string;
  L2_CONTRACT_ADDRESS_BASE: ContractAddress;
  L1_CONTRACT_ADDRESS_BASE?: ContractAddressIdOnly;
  L1_CONTRACT_ABI_BASE?: string;
  L1_CALL_METHOD_NAME_BASE?: string;
  L2_CONTRACT_ADDRESS_MAKER: ContractAddress;
  L1_CONTRACT_ADDRESS_MAKER?: ContractAddressIdOnly;
  L1_CONTRACT_ABI_MAKER?: string;
  L1_CALL_METHOD_NAME_MAKER?: string;
  L2_COLLATERAL_PERCENTAGE_MAKER?: string;
  BETA?: BetaInfo;
}

export type CmPackageKind = 'SINGLETON' | 'DYNAMIC';

export type CmPackageBuildEntry = {
  version: string;
  type: CmPackageKind;
  params?: Record<string, string>;
};

type BuildInfoBase = {
  version: string;
  build: string;
  buildDate: string;
  backendUrl: string;
  explorerUrl: string;
  envId: EnvId;
  apiUrl: string;
  'affiliate-fee-pool.cm': CmPackageBuildEntry;
  'dex-app.cm': CmPackageBuildEntry;
  'market-maker.cm': CmPackageBuildEntry;
  'owner.cm': CmPackageBuildEntry;
  'verifier.cm': CmPackageBuildEntry;
};

type BuildInfoTokens = Partial<Record<Currency, TokenInfo>>;

type RefetchFunction = (options?: RefetchOptions) => Promise<QueryObserverResult<BuildInfo, Error>>;

export type BuildInfo = BuildInfoBase & BuildInfoTokens;

type BuildInfoStore = {
  buildInfo: BuildInfo | undefined;
  refetch: RefetchFunction;
  setBuildInfo: (buildInfo: BuildInfo) => void;
  setRefetch: (refetch: RefetchFunction) => void;
};

const useBuildInfoStore = create<BuildInfoStore>()((set) => ({
  buildInfo: undefined,
  setBuildInfo: (buildInfo) => set({ buildInfo }),
  refetch: async () => Promise.resolve({} as QueryObserverResult<BuildInfo, Error>),
  setRefetch: (refetch) => set({ refetch }),
}));

const useBuildInfo = () => {
  const { setBuildInfo, setRefetch } = useBuildInfoStore();
  const queryKey = ['CwapBuildInfo'];

  const queryFn = async (): Promise<BuildInfo> => {
    const buildInfo = await fetch('/build-info.json', {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    })
      .then((res) => res.json() as Promise<BuildInfo>)
      .catch(() => ({}) as BuildInfo);

    setBuildInfo(buildInfo);

    return buildInfo;
  };

  const query = useQuery<BuildInfo, Error>({ queryKey, queryFn, enabled: true, refetchInterval: 1000 * 30 });

  useEffect(() => {
    setRefetch(query.refetch);
  }, [query.refetch]);

  return query;
};

const useBuildInfoGuard = () => {
  const { buildInfo, refetch } = useBuildInfoStore();

  const withBuildInfoGuard = useCallback(
    <TArgs extends unknown[], TReturn>(callback: (...args: TArgs) => Promise<TReturn>) =>
      async (...args: TArgs): Promise<TReturn> => {
        try {
          if (isDev) {
            return callback(...args);
          }

          const currentBuildInfo = await refetch();

          if (currentBuildInfo.error) {
            throw currentBuildInfo.error;
          }

          if (currentBuildInfo.data?.build !== buildInfo?.build) {
            throw new Error('Build info has been updated. Please reload the page.');
          }

          return callback(...args);
        } catch (error) {
          console.error(error);
          throw error;
        }
      },
    [buildInfo?.build]
  );

  return withBuildInfoGuard;
};

export { useBuildInfo, useBuildInfoGuard };

export default useBuildInfoStore;
